/**
 * mockPlan.js — frontend-only billing state for Talent Connect.
 *
 * Stripe integration is NOT wired yet. This module simulates the Free/Business
 * plan flow purely via localStorage so the user can validate UX before we
 * cable real billing. Anything that touches money lives here so the rip-out
 * once Stripe is ready is a single import swap.
 *
 * Keys (string):
 *   talentconnect:mock-plan         → 'free' | 'business'  (default 'free')
 *   talentconnect:mock-rooms-used   → integer; resets on the 1st of each month
 *                                     in real billing, here it's only cleared
 *                                     via resetMockPlan() / DEV-reset link.
 */
import { useCallback, useEffect, useState } from 'react';

const PLAN_KEY = 'talentconnect:mock-plan';
const USED_KEY = 'talentconnect:mock-rooms-used';

// Phase 15 (TRIAL_LIMIT_OVERRIDE, 1/4) — Free tier is unlimited for demos.
// Was: 3. Flip UNLIMITED_OVERRIDE=false to restore the original quota.
export const FREE_PLAN_LIMIT = Infinity;
export const FREE_CONCURRENT_LIMIT = 1;
export const BUSINESS_PRICE_USD = 599;

/** Phase 15 — when true, all Free-tier paywall banners/hard-gates are bypassed. */
export const UNLIMITED_OVERRIDE = true;

/** Whether to surface the DEV reset link. False on production builds. */
export const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

const safeStorage = () => (typeof localStorage === 'undefined' ? null : localStorage);

export function getMockPlan() {
  const s = safeStorage();
  if (!s) return 'free';
  return s.getItem(PLAN_KEY) === 'business' ? 'business' : 'free';
}

export function getRoomsUsed() {
  const s = safeStorage();
  if (!s) return 0;
  const raw = parseInt(s.getItem(USED_KEY) || '0', 10);
  return Number.isFinite(raw) && raw >= 0 ? raw : 0;
}

export function incrementRoomsUsed() {
  const s = safeStorage();
  const next = getRoomsUsed() + 1;
  if (s) s.setItem(USED_KEY, String(next));
  // Notify same-tab listeners (useMockPlan hook).
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mockplan:changed'));
  }
  return next;
}

export function resetMockPlan() {
  const s = safeStorage();
  if (s) {
    s.removeItem(PLAN_KEY);
    s.removeItem(USED_KEY);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mockplan:changed'));
  }
}

/**
 * DEV helper — flip between 'free' and 'business' without clearing quota.
 * Used by the [DEV] toggle link so the analytics card's two states are
 * viewable in preview without hand-editing localStorage. Returns the new plan.
 */
export function toggleMockPlan() {
  const s = safeStorage();
  const next = getMockPlan() === 'business' ? 'free' : 'business';
  if (s) s.setItem(PLAN_KEY, next);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('mockplan:changed'));
  }
  return next;
}

/** Human-readable date for "limit resets on …" — always the 1st of next month. */
export function nextResetDate() {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return next.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** React hook — reactive plan + quota state, increment + reset helpers. */
export function useMockPlan() {
  const [plan, setPlan] = useState(getMockPlan);
  const [used, setUsed] = useState(getRoomsUsed);

  useEffect(() => {
    const reread = () => {
      setPlan(getMockPlan());
      setUsed(getRoomsUsed());
    };
    window.addEventListener('storage', reread);
    window.addEventListener('mockplan:changed', reread);
    return () => {
      window.removeEventListener('storage', reread);
      window.removeEventListener('mockplan:changed', reread);
    };
  }, []);

  const increment = useCallback(() => {
    const next = incrementRoomsUsed();
    setUsed(next);
    return next;
  }, []);

  const reset = useCallback(() => {
    resetMockPlan();
    setPlan('free');
    setUsed(0);
  }, []);

  const isAtLimit = !UNLIMITED_OVERRIDE && plan === 'free' && used >= FREE_PLAN_LIMIT;

  return {
    plan,
    used,
    increment,
    reset,
    isAtLimit,
    limit: FREE_PLAN_LIMIT,
    // Phase 15 (TRIAL_LIMIT_OVERRIDE) — consumers gate banners/hard-caps on this.
    unlimitedOverride: UNLIMITED_OVERRIDE,
  };
}
